// frontend/src/components/Sidebar.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-02
// Updated:      2026-09-04
//
// Purpose:
//               Provides the primary collapsible navigation sidebar for the
//               Vision Systems administration application.
//
// Role:
//               Renders application navigation, persists open/collapsed state,
//               and provides an independently scrollable navigation area when
//               vertical space is constrained.
//
// Lifecycle Status:
//               Active production component.
//
// Change Summary:
//               • Removed all viewport-height ownership from the sidebar.
//               • Sidebar now consumes only the height allocated by Layout.js.
//               • Added minHeight: 0 throughout the flex hierarchy so browser
//                 zoom and short viewports correctly create navigation overflow.
//               • Navigation region now scrolls independently.
//               • Collapse control remains permanently visible.
//               • Navigation entries cannot vertically shrink.
//               • Prevented horizontal overflow.
//               • Navigation definition moved to shared visionNavigation so
//                 desktop and future mobile navigation use one canonical source.
//
// Previous Updates:
//
// Update (2026-01-27):
//               • Added Quotes nav item routing to /dashboard/quotes.
//
// Update (2026-03-10):
//               • Added Monitor nav item routing to /dashboard/monitor.
//
// Update (2026-04-15):
//               • Added Workflows nav item routing to /dashboard/workflows.
//
// Update (2026-04-20):
//               • Added Cameras nav item routing to /dashboard/cameras.
//
// Future Intent:
//               Sidebar viewport sizing remains controlled exclusively by the
//               parent application shell. Mobile navigation should be treated
//               separately from this desktop navigation implementation.

import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";


import { useTheme } from "../context/ThemeContext";
import visionNavigation from "../navigation/visionNavigation";

const SIDEBAR_STORAGE_KEY = "visionSidebarOpen";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const hasRestoredStoredState = useRef(false);

  useEffect(() => {
    if (hasRestoredStoredState.current) {
      return;
    }

    hasRestoredStoredState.current = true;

    try {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);

      if (stored === null) {
        localStorage.setItem(
          SIDEBAR_STORAGE_KEY,
          isOpen ? "true" : "false"
        );

        return;
      }

      const storedOpen = stored === "true";

      if (
        storedOpen !== isOpen &&
        typeof toggleSidebar === "function"
      ) {
        toggleSidebar();
      }
    } catch {
      // Ignore localStorage failures and retain parent-controlled state.
    }
  }, [isOpen, toggleSidebar]);

  const handleToggleSidebar = () => {
    const nextOpen = !isOpen;

    try {
      localStorage.setItem(
        SIDEBAR_STORAGE_KEY,
        nextOpen ? "true" : "false"
      );
    } catch {
      // Ignore localStorage failures; sidebar can still be toggled.
    }

    if (typeof toggleSidebar === "function") {
      toggleSidebar();
    }
  };

  return (
    <aside
      style={{
        width: isOpen ? "260px" : "80px",

        /*
         * Sidebar height is owned by Layout.js.
         *
         * Do NOT use 100vh / 100dvh here.
         */
        height: "100%",
        minHeight: 0,

        flex: "0 0 auto",

        backgroundColor: "var(--primary-color)",
        color: "#fff",

        transition: "width 0.3s ease-in-out",

        display: "flex",
        flexDirection: "column",

        alignItems: isOpen
          ? "flex-start"
          : "center",

        paddingTop: "1rem",
        paddingLeft: isOpen
          ? "1rem"
          : "10px",
        paddingRight: isOpen
          ? "0"
          : "10px",

        position: "relative",

        borderBottomLeftRadius: "12px",

        /*
         * The aside itself must remain constrained.
         * Only the navigation region below is scrollable.
         */
        overflow: "hidden",

        boxSizing: "border-box",

        fontFamily: theme.fontBody || "sans-serif",
      }}
    >
      <div
        onClick={handleToggleSidebar}
        style={{
          flex: "0 0 auto",

          cursor: "pointer",

          color: "#aaa",

          padding: "10px",

          fontSize: "1.8rem",

          alignSelf: isOpen
            ? "flex-end"
            : "center",

          marginRight: isOpen
            ? "10px"
            : 0,

          marginLeft: isOpen
            ? "10px"
            : 0,

          userSelect: "none",
        }}
        title={
          isOpen
            ? "Collapse sidebar"
            : "Expand sidebar"
        }
      >
        ☰
      </div>

      <nav
        style={{
          width: "100%",

          flex: "1 1 auto",

          /*
           * This is the critical property which permits this child
           * to become smaller than its navigation contents.
           */
          minHeight: 0,

          overflowY: "auto",
          overflowX: "hidden",

          overscrollBehaviorY: "contain",

          boxSizing: "border-box",

          WebkitOverflowScrolling: "touch",
        }}
      >
        {visionNavigation.map((item) => (
          <div
            key={item.path}
            onClick={() => {
              if (item.path !== "#") {
                navigate(item.path);
              }
            }}
            style={{
              display: "flex",

              alignItems: "center",

              justifyContent: isOpen
                ? "flex-start"
                : "center",

              width: "100%",

              boxSizing: "border-box",

              padding: "10px",

              cursor:
                item.path !== "#"
                  ? "pointer"
                  : "default",

              whiteSpace: "nowrap",

              fontSize: isOpen
                ? "1.2rem"
                : "0",

              opacity:
                item.path === "#"
                  ? 0.5
                  : 1,

              /*
               * Navigation entries retain their intended height.
               * Overflow therefore becomes actual scrollable overflow.
               */
              flex: "0 0 auto",
            }}
          >
            <span
              style={{
                marginRight: isOpen
                  ? "12px"
                  : "0",

                display: "flex",

                justifyContent: "center",
                alignItems: "center",

                fontSize: "1.8rem",

                width: isOpen
                  ? "auto"
                  : "100%",

                flex: "0 0 auto",
              }}
            >
              {item.icon}
            </span>

            {isOpen && item.label}
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;
